import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import {
  AddCandidate,
  AddInternalCandidate,
  BioInterest,
  BioStrength,
  Column,
  Compensation,
  Currency,
  CurrencyExchangeRate,
  IAddColumnRequest,
  IBioInterest,
  IBioStrength,
  IColumn,
  IMember,
  IMemberPreferences,
  IOpportunityLocale,
  ISaveOpportunityFeedback,
  IStrength,
  IUpdateColumnRequest,
  Language,
  Member,
  MemberPreferences,
  Opportunity,
  OpportunityDraft,
  OpportunityFeedback,
  OpportunityLanguage,
  SaveInterest,
  SaveOpportunity,
  SaveOpportunityDeadline,
  SaveOpportunityDraft,
  ScreeningQuestion,
  ScreeningQuestionsResponse,
  Strength,
  StrengthExperienceSelection,
  Timezone
} from "@/services/DTOs";
import { IMemberFeedback, MemberFeedback } from "@/services/DTOs/MemberFeedback";
import { OpportunityShare } from "@/services/DTOs/OpportunityShare";
import { IOpportunitySimpleSummary, OpportunitySimpleSummary } from "@/services/DTOs/OpportunitySimpleSummary";
import { OpportunitySummary } from "@/services/DTOs/OpportunitySummary";
import { IPageable } from "@/services/DTOs/Pageable";
import { OpportunitiesApiService } from "@/services/OpportunitiesApiService";
import { trackAPICall } from "@/services/SnowplowService";
import { Dictionary } from "@/utils/Dictionary";
import { getId } from "@/utils/OpportunityUtils";
import { IContextCommand } from "./DTOs/search/SearchCommand";

class Service {
  @trackAPICall("POST", "Opportunities", "opportunity", (args) => ({ share: args[0].share }))
  public add(opportunity: SaveOpportunity): Observable<{ id: string, token: string }> {
    const url = "/api/suite/opportunities";
    return OpportunitiesApiService.post(url, opportunity).pipe(map((response) => (response.data)));
  }

  public get(opportunityId: string): Observable<Opportunity> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}`;
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => new Opportunity(response.data)
    ));
  }

  public getLocale(opportunityId: string): Observable<IOpportunityLocale> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/locale`;
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => response.data as IOpportunityLocale
    ));
  }

  public getForManagerInvitation(opportunityId: string, sharingToken: string): Observable<Opportunity> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/manager-invitation`;
    const params = new URLSearchParams();

    params.append("sharingToken", sharingToken);

    return OpportunitiesApiService.get(url, { params }).pipe(map(
      (response) => new Opportunity(response.data)
    ));
  }

  public getOpportunities(page: number = 0, pageSize?: number): Observable<IPageable<Opportunity>> {
    const url = "/api/suite/opportunities";
    const params = new URLSearchParams();

    params.append("page", page.toString());

    if (pageSize) {
      params.append("pageSize", pageSize.toString());
    }

    return OpportunitiesApiService.get(url, { params })
      .pipe(map((response) =>
        ({ ...response.data, elements: response.data.elements.map((o: Opportunity) => new Opportunity(o)) } as IPageable<Opportunity>)
      ));
  }

  public getOppotunitiesSimpleSummary(opportunitiesIds: string)
    : Observable<IOpportunitySimpleSummary[]> {
    const url = `api/suite/opportunities/${opportunitiesIds}/summaries`;

    return OpportunitiesApiService.get(url).pipe(map((response) => response.data));
  }

  public getOpportunitiesSummaries(query?: string, page: number = 0, pageSize?: number, list: string = "posted"):
    Observable<IPageable<OpportunitySummary>> {
    const url = "/api/suite/opportunities/summaries";
    const params = new URLSearchParams();

    params.append("list", list);
    params.append("page", page.toString());
    if (!!query) {
      params.append("query", query);
    }

    if (pageSize) {
      params.append("pageSize", pageSize.toString());
    }

    return OpportunitiesApiService
      .cancellableGet(url, params, { "Cache-Control": "no-cache" })
      .pipe(map((response) => response.data as IPageable<OpportunitySummary>));
  }

  public getOpportunitiesApplicationsSummaries(status: string[] = [], page: number = 0, pageSize?: number, includeAll: boolean = false)
    : Observable<IPageable<OpportunitySummary>> {
    const url = "/api/suite/opportunities/all/candidates/me/summaries";
    const params = new URLSearchParams();

    params.append("page", page.toString());

    if (pageSize) {
      params.append("pageSize", pageSize.toString());
    }

    if (includeAll) {
      params.append("includeAll", includeAll.toString());
    }

    status.forEach((s) => {
      params.append("status", s);
    });

    return OpportunitiesApiService.get(url, { params })
      .pipe(map((response) => response.data as IPageable<OpportunitySummary>));
  }

  public getPendingCrawledApplicationsSummaries(): Observable<OpportunitySummary[]> {
    const url = "/api/suite/opportunities/pending-crawled/candidates/me/summaries";

    return OpportunitiesApiService.get(url).pipe(map((response) => response.data as OpportunitySummary[]));
  }

  @trackAPICall("PUT", "Opportunities", "opportunity", (args) => ({ share: args[0].share }))
  public update(opportunityId: string, opportunity: object): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}`;
    return OpportunitiesApiService.put(url, opportunity).pipe(map(() => undefined));
  }

  @trackAPICall("PUT", "Opportunities", "deadline", (args) => ({ share: args[0].share }))
  public updateDeadline(opportunityId: string, deadline: SaveOpportunityDeadline): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/deadline`;
    return OpportunitiesApiService.put(url, deadline).pipe(map(() => undefined));
  }

  @trackAPICall("PUT", "Opportunities", "torreImport")
  public requestTorreImport(opportunityId: string, externalInfoSourceUrl: string): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/torre-import`;
    return OpportunitiesApiService.put(url, { externalInfoSourceUrl }).pipe(map(() => undefined));
  }

  @trackAPICall("PUT", "Opportunities", "opportunityToDraft")
  public transformIntoDraft(opportunityId: string): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/to-draft`;
    return OpportunitiesApiService.put(url).pipe(map(() => undefined));
  }

  @trackAPICall("POST", "Opportunities", "draft")
  public addDraft(draft: SaveOpportunityDraft): Observable<string> {
    const url = "/api/suite/drafts";
    return OpportunitiesApiService.post(url, draft).pipe(map((response) => response.data.token));
  }

  public getDraft(token?: string): Observable<OpportunityDraft> {
    let params = new URLSearchParams();
    if (token) {
      params = new URLSearchParams([["token", token]]);
    }

    const url = "/api/suite/drafts";
    return OpportunitiesApiService.cancellableGet(url, params).pipe(map(
      (response) => new OpportunityDraft(response.data)
    ));
  }

  @trackAPICall("PUT", "Opportunities", "draft", (args) => ({ share: args[0].share }))
  public updateDraft(token: string, draft: SaveOpportunityDraft): Observable<void> {
    const url = `/api/suite/drafts/${token}`;
    return OpportunitiesApiService.put(url, draft).pipe(map(() => undefined));
  }

  @trackAPICall("POST", "Opportunities", "feedback")
  public addFeedback(opportunityId: string, feedback: ISaveOpportunityFeedback): Observable<string> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/feedbacks`;
    return OpportunitiesApiService.post(url, feedback).pipe(map((response) => response.data.id));
  }

  public addTracking(opportunityId: string, action: string): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/tracking`;
    return OpportunitiesApiService.post(url, { action }).pipe(map(() => undefined));
  }

  public getBioStrengths(): Observable<BioStrength[]> {
    const url = "/api/suite/bio-strengths";
    return OpportunitiesApiService.get(url).pipe(
      map((response) => response.data.map((data: IBioStrength) => new BioStrength(data)))
    );
  }

  @trackAPICall("POST", "Opportunities", "bioStrengths")
  public addBioStrengths(strengths: Strength[]): Observable<string[]> {
    const url = "/api/suite/bio-strengths";
    return OpportunitiesApiService.post(url, strengths).pipe(map((response) => response.data));
  }

  @trackAPICall("POST", "Opportunities", "candidateCompensation")
  public addCandidateCompensation(opportunityId: string, candidateId: string, compensation: Compensation): Observable<string[]> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/compensation`;
    return OpportunitiesApiService.post(url, compensation).pipe(map((response) => response.data));
  }

  public getBioInterests(): Observable<BioInterest[]> {
    const url = "/api/suite/bio-interests";
    return OpportunitiesApiService.get(url).pipe(
      map((response) => response.data.map((data: IBioInterest) => new BioInterest(data)))
    );
  }

  @trackAPICall("POST", "Opportunities", "bioInterests")
  public addBioInterests(interests: SaveInterest[]): Observable<string[]> {
    const url = "/api/suite/bio-interests";
    return OpportunitiesApiService.post(url, interests).pipe(map((response) => response.data));
  }

  public getOrCreateStrength(term: string, locale: string): Observable<Strength> {
    return OpportunitiesApiService
      .post(`/api/suite/strengths`, { term, locale })
      .pipe(map((response) => new Strength(response.data)));
  }

  public getStrengthsWithContext(query: string, related: number[], context: string, locale: string,
                                 termsContext: IContextCommand, limit: number = 10): Observable<Strength[]> {
    return OpportunitiesApiService
      .post("/api/suite/strengths-context", { query, related, context, locale, termsContext, limit })
      .pipe(map((response) => response.data.map((data: IStrength) => new Strength(data))));
  }

  public getStrengths(query: string, related: number[], context: string, locale: string, limit: number = 10): Observable<Strength[]> {
    const params = new URLSearchParams([["limit", limit.toString()]]);
    if (query && query.trim().length > 0) {
      params.append("q", query.trim());
    }

    if (related.length > 0) {
      related.forEach((value) => {
        params.append("r", value.toString());
      });
    }
    params.append("context", context);
    params.append("locale", locale);
    return OpportunitiesApiService
      .cancellableGet("/api/suite/strengths", params)
      .pipe(map((response) => response.data.map((data: IStrength) => new Strength(data))));
  }

  public addSkill(opportunityId: string, skill: StrengthExperienceSelection): Observable<number> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/strengths`;

    return OpportunitiesApiService.post(url, skill).pipe(map((response) => response.data));
  }

  public getFeedback(opportunityId: string): Observable<OpportunityFeedback> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/feedbacks`;
    return OpportunitiesApiService.get(url).pipe(map((response) => new OpportunityFeedback(response.data)));
  }

  public getTimezones(): Observable<Timezone[]> {
    const url = "/api/suite/timezones";
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => response.data.map((data: Timezone) => new Timezone(data))
    ));
  }

  public getCurrencies(): Observable<Currency[]> {
    const url = "/api/suite/currencies";
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => response.data.map((data: Currency) => new Currency(data))
    ));
  }

  public getCurrenciesExchangeRate(): Observable<CurrencyExchangeRate[]> {
    const url = "/api/suite/currencies-exchange-rate";
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => response.data.map((exchangeRate: any) => new CurrencyExchangeRate(exchangeRate))
    ));
  }

  public getOpportunityMembers(opportunityId: string): Observable<Member[]> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members`;

    return OpportunitiesApiService.get(url).pipe(
      map((response) => response.data.map((data: IMember) => new Member(data)))
    );
  }

  public getCurrentMember(opportunityId: string): Observable<Member> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me`;

    return OpportunitiesApiService.get(url).pipe(map((response) => new Member(response.data)));
  }

  public addShare(share: OpportunityShare): Observable<void> {
    const url = `/api/suite/opportunities/${getId(share.opportunityId)}/shares`;
    return OpportunitiesApiService.post(url, share).pipe(map(() => undefined));
  }

  public getCurrentMemberFeedbackHistory(): Observable<MemberFeedback[]> {
    const url = `/api/suite/people/me/member-feedback`;

    return OpportunitiesApiService.get(url).pipe(map((response) => response.data.map((item: IMemberFeedback) => new MemberFeedback(item))));
  }

  public getDraftCurrentMember(token: string): Observable<Member> {
    const url = `/api/suite/drafts/${token}/members/me`;

    return OpportunitiesApiService.get(url).pipe(map((response) => new Member(response.data)));
  }

  @trackAPICall("DELETE", "Opportunities", "hideCurrentMemberFeedback")
  public hideCurrentMemberFeedback(id: string): Observable<void> {
    const url = `/api/suite/people/me/member-feedback/${id}`;

    return OpportunitiesApiService.delete(url, {}).pipe(map(() => undefined));
  }

  @trackAPICall("PUT", "Opportunities", "currentMember")
  public updateCurrentMember(opportunityId: string, member: Member): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me`;

    return OpportunitiesApiService.put(url, member).pipe(map(() => undefined));
  }

  @trackAPICall("PUT", "Opportunities", "trackPipelineView")
  public trackPipelineView(opportunityId: string): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me/track-pipeline-view`;

    return OpportunitiesApiService.put(url).pipe(map(() => undefined));
  }

  @trackAPICall("PUT", "Opportunities", "updateDismissColumnTip")
  public updateDismissColumnTip(opportunityId: string, tip: string, dismiss: boolean): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me/${tip}/${dismiss}`;

    return OpportunitiesApiService.put(url).pipe(map(() => undefined));
  }

  @trackAPICall("PUT", "Opportunities", "updateSearchIconPipelineViewed")
  public updateSearchIconPipelineViewed(opportunityId: string, viewed: boolean): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me/search-icon-pipeline-viewed/${viewed}`;

    return OpportunitiesApiService.put(url).pipe(map(() => undefined));
  }

  @trackAPICall("PUT", "Opportunities", "updateInterviewColumnAutomessageSuggestionViewed")
  public updateInterviewColumnAutomessageSuggestionViewed(opportunityId: string, viewed: boolean): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me/interview-column-automessage-suggestion/${viewed}`;

    return OpportunitiesApiService.put(url).pipe(map(() => undefined));
  }

  @trackAPICall("PUT", "Opportunities", "currentDraftMember")
  public updateDraftCurrentMember(token: string, member: Member): Observable<void> {
    const url = `/api/suite/drafts/${token}/members/me`;

    return OpportunitiesApiService.put(url, member).pipe(map(() => undefined));
  }

  @trackAPICall("POST", "Opportunities", "currentMember")
  public addMemberForCurrentPerson(opportunityId: string, member: Member): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me`;

    return OpportunitiesApiService.post(url, member).pipe(map(() => undefined));
  }

  @trackAPICall("POST", "Opportunities", "currentDraftMember")
  public addDraftMemberForCurrentPerson(token: string, member: Member): Observable<void> {
    const url = `/api/suite/drafts/${token}/members/me`;

    return OpportunitiesApiService.post(url, member).pipe(map(() => undefined));
  }

  @trackAPICall("POST", "Opportunities", "members")
  public saveTeamMembers(opportunityId: string, members: Member[]): Observable<string[]> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/team`;
    return OpportunitiesApiService.post(url, members).pipe(map((response) => response.data));
  }

  public getMemberByToken(token: string): Observable<Member> {
    const url = `/api/suite/opportunity-members/${token}`;

    return OpportunitiesApiService.get(url).pipe(map((response) => new Member(response.data)));
  }

  @trackAPICall("POST", "Opportunities", "claimMembership")
  public claimMembership(claimedMemberId: string, token: string): Observable<string> {
    const url = `/api/suite/opportunity-members/${token}/member/${claimedMemberId}`;

    return OpportunitiesApiService.post(url, {}).pipe(map((response) => response.data));
  }

  public getCurrentMemberPreferences(opportunityId: string): Observable<MemberPreferences[]> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me/notification-preferences`;

    return OpportunitiesApiService.get(url).pipe(
      map((response) => response.data.map((data: IMemberPreferences) => new MemberPreferences(data)))
    );
  }

  public getDraftCurrentMemberPreferences(token: string): Observable<MemberPreferences[]> {
    const url = `/api/suite/drafts/${token}/members/me/notification-preferences`;

    return OpportunitiesApiService.get(url).pipe(
      map((response) => response.data.map((data: IMemberPreferences) => new MemberPreferences(data)))
    );
  }

  public duplicateOpportunity(opportunityId: string): Observable<string> {
    const url = `/api/suite/opportunities/${opportunityId}/create-prefill`;
    return OpportunitiesApiService.post(url, {}).pipe(map((response) => response.data));
  }

  public publishOpportunity(opportunityId: string, partnershipId?: string): Observable<Record<string, unknown>> {
    const partnershipIdQuery = partnershipId ? `?partnershipId=${partnershipId}` : "";
    const url = `/api/suite/opportunities/${getId(opportunityId)}/publish/true${partnershipIdQuery}`;

    return OpportunitiesApiService.put(url).pipe(map((response) => response.data as Record<string, unknown>));
  }

  public updateOpportunityDeadline(opportunityId: string, deadline: SaveOpportunityDeadline): Observable<Record<string, unknown>> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/deadline`;

    return OpportunitiesApiService.put(url, deadline).pipe(map((response) => response.data));
  }

  @trackAPICall("POST", "Opportunities", "memberPreferences")
  public saveCurrentMemberPreferences(opportunityId: string, preferences: MemberPreferences[]): Observable<string[]> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me/notification-preferences`;

    return OpportunitiesApiService.post(url, preferences).pipe(map((response) => response.data));
  }

  @trackAPICall("POST", "Opportunities", "draftMemberPreferences")
  public saveDraftCurrentMemberPreferences(token: string, preferences: MemberPreferences[]): Observable<string[]> {
    const url = `/api/suite/drafts/${token}/members/me/notification-preferences`;

    return OpportunitiesApiService.post(url, preferences).pipe(map((response) => response.data));
  }

  @trackAPICall("GET", "Opportunities", "board")
  public getBoard(opportunityId: string): Observable<Column[]> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/board`;
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => response.data.map((data: IColumn) => new Column(data))
    ));
  }

  public getCandidatesCountByColumnCodeOrId(opportunityId: string): Observable<Dictionary<number>> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/count`;
    return OpportunitiesApiService.get(url).pipe(map((response) => response.data as Dictionary<number>));
  }

  @trackAPICall("POST", "Opportunities", "column")
  public addColumn(opportunityId: string, name: string, position: number, funnelTag?: string): Observable<string> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/board`;
    const newColumnProperties = { name, position, ...funnelTag ? { funnelTag } : {} } as IAddColumnRequest;
    return OpportunitiesApiService.post(url, newColumnProperties).pipe(map((response) => response.data.id));
  }

  @trackAPICall("PUT", "Opportunities", "column")
  public updateColumn(opportunityId: string, updateRequest: IUpdateColumnRequest): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/board/${updateRequest.id}`;
    return OpportunitiesApiService.put(url, updateRequest).pipe(map(() => undefined));
  }

  @trackAPICall("DELETE", "Opportunities", "column")
  public deleteColumn(columnId: string, opportunityId: string, receiveColumnId?: string): Observable<void> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/board/${columnId}`;
    const urlWithQuery = receiveColumnId ? `${url}?receiveColumnId=${receiveColumnId}` : url ;

    return OpportunitiesApiService.delete(urlWithQuery, {}).pipe(map(() => undefined));
  }

  public getSummariesByIds(ids: string[] = []): Observable<OpportunitySimpleSummary[]> {
    const url = `/api/suite/opportunities/overview/${ids.join(",")}`;
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => response.data.map((data: IOpportunitySimpleSummary) => new OpportunitySimpleSummary(data))
    ));
  }

  public exportResume(opportunityId: string, subjectId: string, candidatePublicId: string): void {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${subjectId}/bio-export`;
    OpportunitiesApiService.getFile(url, candidatePublicId);
  }

  public getQuestions(opportunityId: string, includeDeleted?: boolean): Observable<ScreeningQuestion[]> {
    const params = new URLSearchParams();
    params.append("includeDeleted", includeDeleted ? "true" : "false");
    const url = `/api/suite/opportunities/${getId(opportunityId)}/questions`;
    return OpportunitiesApiService.get(url, { params }).pipe(map(
      (response) => response.data.map((data: ScreeningQuestion) => new ScreeningQuestion(data))
    ));
  }

  @trackAPICall("POST", "Opportunities", "screeningQuestions")
  public addQuestions(opportunityId: string, question: ScreeningQuestion): Observable<string> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/questions `;
    return OpportunitiesApiService.post(url, question).pipe(map((response) => response.data));
  }

  @trackAPICall("PUT", "Opportunities", "screeningQuestions")
  public updateQuestion(opportunityId: string, questionId: string, question: ScreeningQuestion): Observable<string> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/questions/${questionId} `;
    return OpportunitiesApiService.put(url, question).pipe(map((response) => response.data));
  }

  @trackAPICall("DELETE", "Opportunities", "screeningQuestions")
  public deleteQuestion(opportunityId: string, questionId: string): Observable<string> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/questions/${questionId} `;
    return OpportunitiesApiService.delete(url, {}).pipe(map((response) => response.data));
  }

  public getRecentQuestions(opportunityId: string): Observable<ScreeningQuestionsResponse> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/questions/recent `;
    return OpportunitiesApiService.get(url).pipe(map((response) => response.data));
  }

  public getSuggestedQuestions(opportunityId: string): Observable<ScreeningQuestionsResponse> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/questions/suggested `;
    return OpportunitiesApiService.get(url).pipe(map((response) => response.data));
  }

  public getOneClickLoginUrlToContinueRecordingUrl(opportunityId: string): Observable<{ url: string }> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/members/me/continue-url`;
    return OpportunitiesApiService.get(url).pipe(map((response) => response.data));
  }

  @trackAPICall("POST", "Opportunities", "addCandidate")
  public addCandidate(opportunityId: string, candidate: AddCandidate): Observable<{username: string}> {
    const url = `/api/suite/opportunities/${opportunityId}/add-candidate`;
    return OpportunitiesApiService.post(url, candidate).pipe(map((response) => response.data));
  }

  @trackAPICall("POST", "Opportunities", "addInternalCandidate")
  public addInternalCandidate(opportunityId: string, ggId: string, candidate: AddInternalCandidate): Observable<{username: string}> {
    const url = `/api/suite/opportunities/${opportunityId}/add-candidate/${ggId}`;
    return OpportunitiesApiService.post(url, candidate).pipe(map((response) => response.data));
  }

  @trackAPICall("POST", "Opportunities", "uploadCandidateCv")
  public uploadCandidateCv(opportunityId: string, file: FormData): Observable<string> {
    const url = `/api/suite/opportunities/${opportunityId}/candidate-cv`;
    return OpportunitiesApiService.post(url, file, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).pipe(map((value) => value.data));
  }

  public postInteraction(interactionType: string): Observable<void> {
    const url = `/api/suite/users/echo/interaction/${interactionType}`;
    return OpportunitiesApiService.post(url, {}).pipe(map(() => undefined));
  }

  public cancelInteraction(interactionType: string): Observable<void> {
    const url = `/api/suite/users/echo/interaction/${interactionType}`;
    return OpportunitiesApiService.delete(url, {}).pipe(map(() => undefined));
  }

  public getOpportunityEstimateAnnualCompensation(opportunityId: string): Observable<number> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/estimate-annual-compensation`;

    return OpportunitiesApiService.get(url).pipe(
      map((response) => response.data.result)
    );
  }

  public hideOpportunity(opportunityId: string): Observable<string> {
    return OpportunitiesApiService
      .put(`/api/suite/opportunities/${opportunityId}/hide`)
      .pipe(map((response) => response.data));
  }

  public searchLanguages(
    userLocale: string,
    query?: string
  ): Observable<Language[]> {
    const requestUrl = `/api/suite/languages`;
    const params = new URLSearchParams();
    params.append("locale", userLocale);
    params.append("q", query || "");
    return OpportunitiesApiService.get(requestUrl, { params })
      .pipe(map((response) => response.data.map((data: Language) => new Language(data))));
  }

  public addLanguage(opportunityId: string, language: OpportunityLanguage): Observable<string> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/languages`;
    return OpportunitiesApiService.post(url, language).pipe(map((response) => response.data));
  }
}

export const OpportunitiesService = new Service();
